import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { filterVisibleDtoolsSystems } from "./dtoolsOptionVisibility";
import type { DtoolsReviewBuildContext } from "./dtoolsReviewNavigation";
import { resolveSystemDisplayLabel } from "./dtoolsReviewNavigation";
import { resolveDtoolsSystemContentKey } from "./dtoolsProposalContent";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import {
  DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION,
  readDtoolsSystemClassifications,
  readDtoolsTabModelVersion,
  resolveSystemClassification,
  shouldUseDtoolsV2CustomerTabModel,
  type DtoolsSystemClassificationsMap,
  type DtoolsTabModelVersion,
} from "./dtoolsSystemClassification";

export type DtoolsV2CustomerProposalTabId = "clarity" | "summary" | `independent-${string}` | `group-${string}` | "terms";

export type DtoolsV2ClassifiedSystem = {
  system: DtoolsReviewSystem;
  hierarchyIndex: number;
  contentKey: string;
  classification: "independent" | "alternative";
  groupName: string | null;
};

export type DtoolsV2IndependentProposalSection = {
  kind: "independent";
  sectionKey: string;
  title: string;
  system: DtoolsReviewSystem;
  hierarchyIndex: number;
};

export type DtoolsV2AlternativeOption = {
  optionKey: string;
  label: string;
  system: DtoolsReviewSystem;
  hierarchyIndex: number;
};

export type DtoolsV2AlternativeGroup = {
  kind: "alternative-group";
  groupKey: string;
  groupName: string;
  options: DtoolsV2AlternativeOption[];
};

export type DtoolsV2ProposalSection = DtoolsV2IndependentProposalSection | DtoolsV2AlternativeGroup;

export type DtoolsV2CustomerProposalView = {
  version: 2;
  tabModelVersion: typeof DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION;
  tabs: Array<{ id: DtoolsV2CustomerProposalTabId; label: string }>;
  independentSections: DtoolsV2IndependentProposalSection[];
  alternativeGroups: DtoolsV2AlternativeGroup[];
  proposalSections: DtoolsV2ProposalSection[];
};

export { shouldUseDtoolsV2CustomerTabModel, usesDtoolsV2CustomerTabModel } from "./dtoolsSystemClassification";

export function resolveDtoolsCustomerTabModelVersion(input: {
  tabModelVersion?: DtoolsTabModelVersion | null;
  parsedMetadataJson?: unknown;
}): DtoolsTabModelVersion {
  if (input.tabModelVersion != null) return input.tabModelVersion;
  return readDtoolsTabModelVersion(input.parsedMetadataJson);
}

export function resolveDtoolsCustomerClassifications(input: {
  systemClassifications?: DtoolsSystemClassificationsMap;
  parsedMetadataJson?: unknown;
}): DtoolsSystemClassificationsMap {
  if (input.systemClassifications) return input.systemClassifications;
  return readDtoolsSystemClassifications(input.parsedMetadataJson);
}

export function buildDtoolsV2CustomerNavTabs(input: {
  independentSections: DtoolsV2IndependentProposalSection[];
  alternativeGroups: DtoolsV2AlternativeGroup[];
  displaySettings?: DtoolsDisplaySettings;
}): Array<{ id: DtoolsV2CustomerProposalTabId; label: string }> {
  const items: Array<{ id: DtoolsV2CustomerProposalTabId; label: string }> = [];
  if (!input.displaySettings?.hideClarityIntroduction) {
    items.push({ id: "clarity", label: "Clarity" });
  }
  items.push({ id: "summary", label: "System Summary" });
  for (const section of input.independentSections) {
    items.push({
      id: `independent-${section.sectionKey}`,
      label: section.title,
    });
  }
  for (const group of input.alternativeGroups) {
    items.push({
      id: `group-${group.groupKey}`,
      label: group.groupName,
    });
  }
  items.push({ id: "terms", label: "Terms and Conditions" });
  return items;
}

/** @deprecated Use buildDtoolsV2CustomerNavTabs — retained for legacy callers/tests. */
export function buildDtoolsV2CustomerTabItems(
  displaySettings?: DtoolsDisplaySettings,
): Array<{ id: DtoolsV2CustomerProposalTabId; label: string }> {
  const items: Array<{ id: DtoolsV2CustomerProposalTabId; label: string }> = [];
  if (!displaySettings?.hideClarityIntroduction) {
    items.push({ id: "clarity", label: "Clarity" });
  }
  items.push({ id: "terms", label: "Terms and Conditions" });
  return items;
}

export function partitionSystemsByClassification(
  systems: DtoolsReviewSystem[],
  classifications: DtoolsSystemClassificationsMap,
  displaySettings?: DtoolsDisplaySettings,
  audience: "customer" | "staff" = "customer",
): {
  independent: DtoolsV2ClassifiedSystem[];
  alternative: DtoolsV2ClassifiedSystem[];
} {
  const independent: DtoolsV2ClassifiedSystem[] = [];
  const alternative: DtoolsV2ClassifiedSystem[] = [];

  for (const { system, optionIndex } of filterVisibleDtoolsSystems(systems, displaySettings, { audience })) {
    const entry = resolveSystemClassification(classifications, system.systemId, system.name);
    const contentKey = resolveDtoolsSystemContentKey(system);
    const classified: DtoolsV2ClassifiedSystem = {
      system,
      hierarchyIndex: optionIndex,
      contentKey,
      classification: entry.classification,
      groupName: entry.groupName?.trim() || null,
    };
    if (entry.classification === "alternative") alternative.push(classified);
    else independent.push(classified);
  }

  return { independent, alternative };
}

export function buildIndependentProposalSections(
  independent: DtoolsV2ClassifiedSystem[],
  reviewContext?: DtoolsReviewBuildContext,
): DtoolsV2IndependentProposalSection[] {
  return independent.map((item) => ({
    kind: "independent",
    sectionKey: item.contentKey,
    title: resolveSystemDisplayLabel(item.system, reviewContext, item.hierarchyIndex),
    system: item.system,
    hierarchyIndex: item.hierarchyIndex,
  }));
}

export function buildAlternativeGroups(
  alternative: DtoolsV2ClassifiedSystem[],
  reviewContext?: DtoolsReviewBuildContext,
): DtoolsV2AlternativeGroup[] {
  const groupOrder: string[] = [];
  const groupMap = new Map<string, DtoolsV2AlternativeGroup>();

  for (const item of alternative) {
    const groupName = item.groupName?.trim() || item.system.name.trim() || "Alternatives";
    const groupKey = groupName.toLowerCase();
    let group = groupMap.get(groupKey);
    if (!group) {
      group = { kind: "alternative-group", groupKey, groupName, options: [] };
      groupMap.set(groupKey, group);
      groupOrder.push(groupKey);
    }
    group.options.push({
      optionKey: item.contentKey,
      label: resolveSystemDisplayLabel(item.system, reviewContext, item.hierarchyIndex),
      system: item.system,
      hierarchyIndex: item.hierarchyIndex,
    });
  }

  return groupOrder.map((key) => groupMap.get(key)!).filter(Boolean);
}

export function buildV2CustomerProposalView(input: {
  systems: DtoolsReviewSystem[];
  classifications: DtoolsSystemClassificationsMap;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  audience?: "customer" | "staff";
}): DtoolsV2CustomerProposalView {
  const audience = input.audience ?? "customer";
  const { independent, alternative } = partitionSystemsByClassification(
    input.systems,
    input.classifications,
    input.displaySettings,
    audience,
  );
  const independentSections = buildIndependentProposalSections(independent, input.reviewContext);
  const alternativeGroups = buildAlternativeGroups(alternative, input.reviewContext);

  return {
    version: 2,
    tabModelVersion: DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION,
    tabs: buildDtoolsV2CustomerNavTabs({
      independentSections,
      alternativeGroups,
      displaySettings: input.displaySettings,
    }),
    independentSections,
    alternativeGroups,
    proposalSections: [...independentSections, ...alternativeGroups],
  };
}
